/**
 * odl-app write endpoints — the new site-scoped mutating actions driven by
 * the odl-app contract layer.
 *
 * Covers:
 * - unit command catalog fetch + dispatch (relay on/off, intrusion arm/disarm, capture, reboot, palette)
 * - unit configuration fetch + patch (CIC settings, thresholds, relay params)
 * - unit action catalog fetch + dispatch (server-side unit ops, e.g. change-device)
 * - alarm clear (DELETE per alarm state id)
 * - maintenance log create + update
 * - relay config (loads / relay settings / schedules)
 * - relay load contracts
 * - camera create / patch / delete (write-only; reads come from socket snapshot)
 * - camera schedule create / patch / delete
 * - site identity patch (PATCH /sites/{id}/)
 * - site association list + action dispatch (share / unshare / move / deactivate)
 * - site clone options fetch + queue clone
 * - generic odl-app list + stats fetch for runtime-resolved grid resources
 */
import apiClient from '@sophocles/http-common';

// ── Generic grid fetch ─────────────────────────────────────────────────────────

/**
 * GET an odl_app path and hand back the response body untouched.
 *
 * The grid fetchers below are named for the route each serves rather than for
 * this mechanism, so a call site reads as what it is asking for. They differ
 * only in the body shape their route answers with, which the caller unwraps —
 * so they share one implementation and cannot drift apart.
 *
 * @param {string} path  Full API path including `odl-app/` prefix.
 * @param {object} [params]  Serialized query params (JSON:API wire format).
 * @returns {Promise<object>}
 */
const fetchOdlAppBody = (path, params) => apiClient.get(`${path}/`, { params }).then(res => res.data);

/**
 * Fetch a paginated odl_app list resource by full path.
 *
 * GET /api/<path>/
 * Path must already carry the `odl-app/` prefix and resource segment (e.g.
 * `odl-app/sites/1/cameras`) — the generic EntityGrid config builds it from
 * basePath before calling this.
 *
 * @type {(path: string, params?: object) => Promise<{data: object[], meta: object}>}
 */
export const fetchResourceList = fetchOdlAppBody;

/**
 * Fetch the aggregate counts behind an odl_app grid's stat cards.
 *
 * GET /api/<path>/
 * Path must already carry the `odl-app/` prefix, the resource segment and the
 * `stats` segment (e.g. `odl-app/sites/stats`) — EntityGrid derives it from
 * basePath before calling this. The body is bare (NOT `{data}`-wrapped): a
 * mapping of the dotted paths the grid's stat cards name to their numbers.
 *
 * @type {(path: string, params?: object) => Promise<object>}
 */
export const fetchResourceStats = fetchOdlAppBody;

// ── Site commands ──────────────────────────────────────────────────────────────

/**
 * Fetch the available commands catalog for a site.
 *
 * GET /api/odl-app/sites/<siteId>/unit/commands/
 * Returns the sections + commands available for the device (role + firmware gated
 * server-side).
 *
 * @param {number|string} siteId
 * @returns {Promise<object>}
 */
export const fetchUnitCommands = siteId =>
  apiClient.get(`/odl-app/sites/${siteId}/unit/commands/`).then(res => res.data.data);

/**
 * Dispatch one or more commands to a site device.
 *
 * POST /api/odl-app/sites/<siteId>/unit/commands/
 * Body: { commands: [{ key, params }] }
 * Returns the bare batch result { pending_commands: [...], errors: [...] } —
 * NOT wrapped in the {data} envelope (the dispatch batch shape is its own).
 *
 * HTTP 200 = all queued; 207 = partial success; 400 = nothing queued.
 *
 * @param {number|string} siteId
 * @param {Array<{key: string, params?: object}>} commands
 * @returns {Promise<{ pending_commands: object[], errors: object[] }>}
 */
export const dispatchUnitCommands = (siteId, commands) =>
  apiClient.post(`/odl-app/sites/${siteId}/unit/commands/`, { commands }).then(res => res.data);

// ── Alarm ─────────────────────────────────────────────────────────────────────

/**
 * Clear a single active alarm state.
 *
 * DELETE /api/odl-app/sites/<siteId>/alarms/<alarmStateId>/
 * Returns 204 no-body.
 *
 * @param {number|string} siteId
 * @param {number|string} alarmStateId
 * @returns {Promise<void>}
 */
export const clearSiteAlarmState = (siteId, alarmStateId) =>
  apiClient.delete(`/odl-app/sites/${siteId}/alarms/${alarmStateId}/`);

// ── Relay config ──────────────────────────────────────────────────────────────

/**
 * Write relay config (loads, relay settings, schedules, relaycontroller params)
 * for a site using the new odl-app contract.
 *
 * POST /api/odl-app/site-relay-config/<siteId>/
 * Body: { relaycontroller?, relay_monitor?, actions: [{ loads, relays, schedules }] }
 * The backend emits a 'unit' socket event on success; no Redux refresh needed.
 *
 * @param {number|string} siteId
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const postSiteRelayConfig = (siteId, payload) =>
  apiClient.post(`/odl-app/site-relay-config/${siteId}/`, payload).then(res => res.data);

// ── Maintenance log ───────────────────────────────────────────────────────────

/**
 * Create a new maintenance log entry.
 *
 * POST /api/odl-app/sites/<siteId>/maintenance-logs/
 * Body fields: { issue, date_reported, ... }
 *
 * @param {number|string} siteId
 * @param {object} data
 * @returns {Promise<object>}
 */
export const createMaintenanceLog = (siteId, data) =>
  apiClient.post(`/odl-app/sites/${siteId}/maintenance-logs/`, data).then(res => res.data.data);

/**
 * Update an existing maintenance log entry.
 *
 * PATCH /api/odl-app/sites/<siteId>/maintenance-logs/<id>/
 *
 * @param {number|string} siteId
 * @param {number|string} id
 * @param {object} data
 * @returns {Promise<object>}
 */
export const patchMaintenanceLog = (siteId, id, data) =>
  apiClient.patch(`/odl-app/sites/${siteId}/maintenance-logs/${id}/`, data).then(res => res.data.data);

/**
 * Dispatch one or more server-side unit actions.
 *
 * POST /api/odl-app/sites/<siteId>/unit/actions/
 * Body: { commands: [{ key, params }] }
 * Returns the bare batch result array (NOT {data}-wrapped). Each entry has a
 * `status` field — callers must inspect entries for per-item errors.
 * HTTP 200 = all applied; 207 = partial; 400 = all failed.
 *
 * @param {number|string} siteId
 * @param {Array<{key: string, params?: object}>} commands
 * @returns {Promise<object[]>}
 */
export const dispatchUnitActions = (siteId, commands) =>
  apiClient.post(`/odl-app/sites/${siteId}/unit/actions/`, { commands }).then(res => res.data?.data ?? res.data);

// ── Cameras ───────────────────────────────────────────────────────────────────

/**
 * Create a camera on a site's CIC.
 *
 * POST /api/odl-app/sites/<siteId>/cameras/
 * Cameras are read from the socket snapshot (unit.cic.cameras); this endpoint
 * is write-only. The BE emits the `unit` contract on success — no local
 * refresh needed.
 *
 * @param {number|string} siteId
 * @param {{
 *   camera_number: number,
 *   description?: string,
 *   mirrored_vertically?: boolean,
 *   mirrored_horizontally?: boolean,
 *   is_rotated?: boolean,
 *   rotation_amount?: number,
 *   motion_alarm_enabled?: boolean,
 *   pointing_at_relay_load?: { type: string, id: number } | null
 * }} data
 * @returns {Promise<object>}
 */
export const createCamera = (siteId, data) =>
  apiClient.post(`/odl-app/sites/${siteId}/cameras/`, data).then(res => res.data.data);

/**
 * Partially update a camera.
 *
 * PATCH /api/odl-app/sites/<siteId>/cameras/<camId>/
 * All fields optional. When `motion_alarm_enabled` changes the BE queues
 * CIC_CONFIG and emits the `unit` contract. Other field changes persist
 * silently (no command queued).
 *
 * @param {number|string} siteId
 * @param {number|string} camId
 * @param {object} data  Partial camera fields.
 * @returns {Promise<object>}
 */
export const patchCamera = (siteId, camId, data) =>
  apiClient.patch(`/odl-app/sites/${siteId}/cameras/${camId}/`, data).then(res => res.data.data);

/**
 * Delete a camera from a site's CIC.
 *
 * DELETE /api/odl-app/sites/<siteId>/cameras/<camId>/
 * Returns 204 no-body. The BE should emit the `unit` contract (verify against
 * legacy viewset before relying on the socket update for this path).
 *
 * @param {number|string} siteId
 * @param {number|string} camId
 * @returns {Promise<void>}
 */
export const deleteCamera = (siteId, camId) => apiClient.delete(`/odl-app/sites/${siteId}/cameras/${camId}/`);

// ── Camera schedules ──────────────────────────────────────────────────────────

/**
 * Create a capture schedule for a camera.
 *
 * POST /api/odl-app/sites/<siteId>/cameras/<camId>/schedules/
 * Schedules are read from the snapshot (unit.cic.cameras[].schedules[]).
 * `days` is a bitfield: Monday = bit 1 (value 2) … Sunday = bit 7 (value 128);
 * 0xFE (254) = all days. Schedule changes are applied at device check-in —
 * no CIC_CONFIG command is queued — but the write emits the `unit` contract
 * so dashboards refresh live.
 *
 * @param {number|string} siteId
 * @param {number|string} camId
 * @param {{ time?: string, days?: number, interval?: number | null, enabled?: boolean }} data
 * @returns {Promise<object>}
 */
export const createCameraSchedule = (siteId, camId, data) =>
  apiClient.post(`/odl-app/sites/${siteId}/cameras/${camId}/schedules/`, data).then(res => res.data.data);

/**
 * Partially update a camera schedule.
 *
 * PATCH /api/odl-app/sites/<siteId>/cameras/<camId>/schedules/<schedId>/
 * All fields optional. No CIC_CONFIG — applied at check-in; emits the `unit`
 * contract so dashboards refresh live.
 *
 * @param {number|string} siteId
 * @param {number|string} camId
 * @param {number|string} schedId
 * @param {object} data  Partial schedule fields.
 * @returns {Promise<object>}
 */
export const patchCameraSchedule = (siteId, camId, schedId, data) =>
  apiClient.patch(`/odl-app/sites/${siteId}/cameras/${camId}/schedules/${schedId}/`, data).then(res => res.data.data);

/**
 * Delete a camera schedule.
 *
 * DELETE /api/odl-app/sites/<siteId>/cameras/<camId>/schedules/<schedId>/
 * Returns 204 no-body. No CIC_CONFIG — applied at check-in; emits the `unit`
 * contract so dashboards refresh live.
 *
 * @param {number|string} siteId
 * @param {number|string} camId
 * @param {number|string} schedId
 * @returns {Promise<void>}
 */
export const deleteCameraSchedule = (siteId, camId, schedId) =>
  apiClient.delete(`/odl-app/sites/${siteId}/cameras/${camId}/schedules/${schedId}/`);

/**
 * Dispatch association actions for a site (share / unshare / move / deactivate).
 *
 * POST /api/odl-app/sites/<siteId>/associations/
 * Body: { commands: [{ key, params }] } — key is one of share / unshare /
 * move / deactivate. Returns the bare batch result array (NOT {data}-wrapped).
 * Each entry has a `status` field — callers must inspect entries for per-item errors.
 * HTTP 200 = all applied; 207 = partial; 400/403 = whole-request failure.
 *
 * Action params:
 *  - share:      { scope: 'internal'|'third_party', site_group: { type, id } }
 *  - unshare:    { membership: { type, id } }
 *  - move:       { site_group: { type, id } }
 *  - deactivate: { radio_action?: 'inventory'|'purge' }
 *
 * @param {number|string} siteId
 * @param {Array<{key: string, params: object}>} commands
 * @returns {Promise<object[]>}
 */
export const dispatchSiteAssociationActions = (siteId, commands) =>
  apiClient.post(`/odl-app/sites/${siteId}/associations/`, { commands }).then(res => res.data.data);

// ── Site identity ─────────────────────────────────────────────────────────────

/**
 * Partially update core site identity fields (name, description, location, etc.).
 *
 * PATCH /api/odl-app/sites/<siteId>/
 * Returns the updated site object — {data}-wrapped.
 *
 * @param {number|string} siteId
 * @param {object} data  Partial site fields.
 * @returns {Promise<object>}
 */
export const patchSite = (siteId, data) =>
  apiClient.patch(`/odl-app/sites/${siteId}/`, data).then(res => res.data.data);

// ── Site clone ────────────────────────────────────────────────────────────────

/**
 * Fetch clone options and available destination sites.
 *
 * GET /api/odl-app/sites/<siteId>/clone/
 * Returns the raw ModelCloneOptions schema for the source site's model,
 * pre-filtered to the entries with enabled:true (the options this model
 * actually supports), plus the list of visible destination sites within the
 * same primary site-group. Each option entry is
 * `{ label, enabled, fields: [{field, label}], options?: [{field, label, default}], relays?: [relayNumber] }`
 * — `fields` drives the per-field clone toggles, `options` is the legacy
 * "Clone Options" advanced subsection, and `relays` is the option's own
 * eligible relay numbers.
 *
 * @param {number|string} siteId
 * @returns {Promise<{ options: object, available_destinations: object[] }>}
 */
export const fetchCloneOptions = siteId => apiClient.get(`/odl-app/sites/${siteId}/clone/`).then(res => res.data.data);

/**
 * Queue a clone job to copy unit configuration to one or more destination sites.
 *
 * POST /api/odl-app/sites/<siteId>/clone/
 * Async (Celery); responds 202. Returns { data: { task_id, status, destinations[] } }
 * — the response IS {data}-wrapped. Poll or use the socket for completion; the
 * `unit` contract is emitted on each successfully cloned destination.
 *
 * Each `options` entry is the task-native shape: `{enabled, relays: [int],
 * fields: [{field, enabled}], options: [{field, enabled}]}`. Field names in
 * both `fields` and `options` are validated server-side against the source
 * site's clone schema — an unknown field name is rejected with 400 before
 * the task is queued.
 *
 * @param {number|string} siteId
 * @param {{
 *   options: Record<string, {
 *     enabled: boolean,
 *     relays?: number[],
 *     fields?: Array<{ field: string, enabled: boolean }>,
 *     options?: Array<{ field: string, enabled: boolean }>,
 *   }>,
 *   destinations: Array<{ type: string, id: number }>
 * }} payload
 * @returns {Promise<{ task_id: string, status: string, destinations: object[] }>}
 */
export const queueClone = (siteId, payload) =>
  apiClient.post(`/odl-app/sites/${siteId}/clone/`, payload).then(res => res.data);

// ── Sensor config ─────────────────────────────────────────────────────────────

/**
 * Partially update a unit's normalized sensor configuration.
 *
 * PATCH /api/odl-app/sites/<siteId>/unit/sensors-config/
 *
 * @param {number|string} siteId
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const patchSiteSensorConfig = (siteId, payload) =>
  apiClient.patch(`/odl-app/sites/${siteId}/unit/sensors-config/`, payload).then(res => res.data);

// ── Intrusion config ──────────────────────────────────────────────────────────

/**
 * Partially update a unit's normalized intrusion configuration.
 *
 * PATCH /api/odl-app/sites/<siteId>/unit/intrusion-config/
 *
 * @param {number|string} siteId
 * @param {object} payload
 * @returns {Promise<object>}
 */
export const patchSiteIntrusionConfig = (siteId, payload) =>
  apiClient.patch(`/odl-app/sites/${siteId}/unit/intrusion-config/`, payload).then(res => res.data);
